/**
 * IDI-681 D1 (as IDI-682 D1): a login is deactivated when list_users reports
 * its status as "inactive" (Cognito Enabled=false — what Deactivate produces)
 * or "deactivated". Invited logins read "active"; every other status stays
 * offered by the pickers.
 */
export function isDeactivatedStatus(status) {
  const s = (status || '').toString().toLowerCase();
  return s === 'inactive' || s === 'deactivated';
}

/** Set of ids (as strings) of the deactivated users in a list_users result. */
export function deactivatedUserIds(users) {
  const out = new Set();
  (users || []).forEach((u) => {
    if (!u || !isDeactivatedStatus(u.status)) return;
    if (u.id) out.add(String(u.id));
  });
  return out;
}

/**
 * Stored {id, name} pairs from a customer record's salesperson fields (either
 * `salespersons` or `salesperson`, array or single). Plain-string legacy
 * entries and entries without an id are skipped.
 */
export function storedSalespersons(record) {
  if (!record) return [];
  const out = [];
  [record.salespersons, record.salesperson].forEach((field) => {
    const arr = Array.isArray(field) ? field : (field ? [field] : []);
    arr.forEach((sp) => {
      if (sp && typeof sp === 'object' && sp.id) out.push({ id: sp.id, name: sp.name });
    });
  });
  return out;
}

/**
 * IDI-704: records that store bare ids (calendar event assignees, crew
 * members) carry no names. Pair each stored id with the name list_users
 * reports for it now (name, falling back to email) so deactivatedChipOptions
 * can label the kept chip. Ids list_users does not return are skipped.
 */
export function storedIdsWithUserNames(ids, users) {
  const byId = new Map();
  (users || []).forEach((u) => {
    if (u && u.id != null) byId.set(String(u.id), u);
  });
  const out = [];
  (Array.isArray(ids) ? ids : []).forEach((id) => {
    if (id == null || id === '') return;
    const u = byId.get(String(id));
    if (!u) return;
    out.push({ id, name: u.name || u.email || '' });
  });
  return out;
}

/**
 * IDI-681 D2: chip-only, iconed options for people kept on a record who are
 * deactivated. `stored` is [{ id, name }] as saved on the record; an entry
 * becomes an option only when its id is deactivated and not already offered.
 * The label is the saved name (never the id, never "Deactivated").
 */
export function deactivatedChipOptions(stored, options, deactivatedIds) {
  const offered = new Set((options || []).map((o) => String(o.value)));
  const out = [];
  const seen = new Set();
  (stored || []).forEach((p) => {
    const id = p && p.id != null ? String(p.id) : '';
    if (!id || offered.has(id) || seen.has(id)) return;
    if (!deactivatedIds || !deactivatedIds.has(id)) return;
    seen.add(id);
    out.push({
      value: p.id,
      label: p.name || id,
      chipOnly: true,
      deactivated: true,
    });
  });
  return out;
}
